import { useState, useEffect } from 'react';
import { Box, CircularProgress } from 'src/components';

export default function RetryImage({
    src,
    placeholder,
    placeholderDefault = false,
    mode = 'img', // 'img' or 'bg'
    maxRetries = 4,
    interval = 2500,
    showLoader = false,
    style = {},
    children,
    ...otherProps
    }) {
    const [currentSrc, setCurrentSrc] = useState(
        placeholderDefault ? placeholder : (src || placeholder || ''));
    const [retryCount, setRetryCount] = useState(0);
    const [loading, setLoading] = useState(showLoader);

    useEffect(() => {
        if (!src) return;

        let cancelled = false;

        const checkImage = async () => {
            try {
                const res = await fetch(src, { method: 'HEAD' });
                if (res.ok) {
                    if (!cancelled) {
                        setCurrentSrc(src);
                    }
                } else {
                    throw new Error('Image not ready');
                }
            } catch {
                if (!cancelled && retryCount < maxRetries) {
                    setCurrentSrc(placeholder || '');
                    setTimeout(() => {
                        setRetryCount(prev => prev + 1);
                    }, interval);
                }
            } finally {
                if (!cancelled) {
                    setTimeout(() => {
                        setLoading(false);
                    }, 1000);
                }
            }
        };

        checkImage();

        return () => {
            cancelled = true;
        };
    }, [src, retryCount, maxRetries, interval, placeholder]);

    const LoaderOverlay = () =>
        loading && (
            <Box
                sx={{
                position: 'absolute',
                inset: 0,
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                zIndex: 2,
                }}
            >
                <CircularProgress size={20} thickness={4}/>
            </Box>
    );

    if (mode === 'bg') {
        return (
            <Box
                sx={{
                position: 'relative',
                ...style,
                }}
                {...otherProps}>
                <Box
                    sx={{
                        position: 'absolute',
                        inset: 0,
                        backgroundImage: `url(${currentSrc})`,
                        backgroundSize: 'cover',
                        backgroundPosition: 'center',
                        backgroundRepeat: 'no-repeat',
                        zIndex: 0
                    }}/>
                    {children ? children : null}
                    {showLoader && <LoaderOverlay />}
            </Box>
        );
    }

    // default mode: img
    return (
        <Box sx={{ position: 'relative', ...style }}>
            <img
                src={currentSrc}
                style={{ 
                    width: '100%', 
                    height: '100%', 
                    objectFit: 'cover', 
                    backgroundColor: '#fff',
                    ...style }}
                {...otherProps}
                />
                {showLoader && <LoaderOverlay />}
        </Box>
    );
}
